import { Button, Chip, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { IClassFilterDraft } from "ts/interfaces/AdminDashboard"

import { classFilterButtonSx } from "./AdminDashboard.styles"
import useClassOptions from "./useClassOptions"

// Above the table: the applied classes (solid) and the draft's changes (dashed: added; struck
// through: removed). While the draft differs, Apply / Discard sit here too, reachable by keyboard.
const ClassFilterChips = ({
  filter,
  summaryId,
}: {
  filter: IClassFilterDraft
  summaryId: string
}) => {
  const { t } = useTranslation()
  const { applied, draft, setDraft, isDirty, apply, discard } = filter
  const { classes, options } = useClassOptions()
  const shown = classes
    ? options.filter(
        (option) => applied.includes(option.id) || draft.includes(option.id),
      )
    : []

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
      <Typography
        id={summaryId}
        variant="body3"
        color="mockup.neutral30"
        fontWeight={500}
        sx={{ mr: 0.5 }}
      >
        {applied.length
          ? t("adminDashboard.showingClasses", { count: applied.length })
          : t("adminDashboard.showingAllClasses")}
      </Typography>
      {shown.map((option) => {
        const isApplied = applied.includes(option.id)
        const isInDraft = draft.includes(option.id)
        const isPendingRemove = isApplied && !isInDraft
        const toggle = () =>
          setDraft(
            isInDraft
              ? draft.filter((id) => id !== option.id)
              : [...draft, option.id],
          )
        const name = { name: option.name }
        const ariaLabel = isPendingRemove
          ? t("adminDashboard.restoreClass", name)
          : isApplied
            ? t("adminDashboard.removeClass", name)
            : t("adminDashboard.removePendingClass", name)
        return (
          <Chip
            key={option.id}
            size="small"
            color="primary"
            variant="outlined"
            label={option.name}
            aria-label={ariaLabel}
            onClick={toggle}
            onDelete={isPendingRemove ? undefined : toggle}
            sx={
              isApplied && isInDraft
                ? undefined
                : {
                    borderStyle: "dashed",
                    ...(isPendingRemove && {
                      textDecoration: "line-through",
                      color: "mockup.neutral30",
                      borderColor: "mockup.neutral40",
                    }),
                  }
            }
          />
        )
      })}
      {isDirty ? (
        <>
          <Button
            variant="contained"
            onClick={() => apply(draft)}
            sx={classFilterButtonSx}
          >
            {draft.length
              ? t("adminDashboard.applyClasses", { count: draft.length })
              : t("adminDashboard.apply")}
          </Button>
          <Button variant="text" onClick={discard} sx={classFilterButtonSx}>
            {t("adminDashboard.discardClasses")}
          </Button>
        </>
      ) : (
        applied.length > 0 && (
          <Button variant="text" onClick={() => apply([])} sx={classFilterButtonSx}>
            {t("adminDashboard.clearAllClasses")}
          </Button>
        )
      )}
    </Stack>
  )
}

export default ClassFilterChips
